import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = [
    "status", 
    "completionBar", 
    "completionText",
    "totalItems",
    "completedItems",
    "inProgressItems",
    "pendingItems",
    "notification"
  ]
  static values = { projectId: String }
  
  connect() {
    this.setupChannel()
  }
  
  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe()
    }
  }
  
  setupChannel() {
    this.channel = consumer.subscriptions.create(
      { 
        channel: "MeetingProjectsChannel",
        project_id: this.projectIdValue
      },
      {
        connected: () => {
          console.log("Connected to MeetingProjectsChannel")
          this.updateConnectionStatus(true)
        },
        
        disconnected: () => {
          console.log("Disconnected from MeetingProjectsChannel")
          this.updateConnectionStatus(false)
        },
        
        received: (data) => {
          this.handleUpdate(data)
        }
      }
    )
  }
  
  handleUpdate(data) {
    switch(data.type) {
      case 'connection_established':
        this.showNotification('Connected to real-time updates', 'success')
        break
        
      case 'project_update':
        this.updateProjectInfo(data.project)
        break
        
      case 'summary_update':
        this.updateSummary(data.summary)
        break
        
      case 'action_item_update':
        this.updateActionItem(data.action_item)
        this.updateStats(data.stats)
        break
        
      case 'processing_status':
        this.updateProcessingStatus(data)
        break
        
      case 'analysis_complete':
        this.handleAnalysisComplete(data)
        break
        
      case 'status_update':
        this.updateStats(data.stats)
        break
        
      case 'action_items_list':
        this.updateActionItemsList(data.action_items)
        break
    }
  }
  
  updateConnectionStatus(connected) {
    if (this.hasStatusTarget) {
      if (connected) {
        this.statusTarget.classList.remove('hidden')
        this.statusTarget.classList.add('badge-success')
        this.statusTarget.classList.remove('badge-error')
        this.statusTarget.textContent = 'Live'
      } else {
        this.statusTarget.classList.add('badge-error')
        this.statusTarget.classList.remove('badge-success')
        this.statusTarget.textContent = 'Disconnected'
      }
    }
  }
  
  updateProjectInfo(project) {
    // Update project status if displayed
    const statusElement = document.querySelector(`[data-project-status="${project.id}"]`)
    if (statusElement) {
      statusElement.textContent = project.status
    }
    
    // Update last modified
    const modifiedElement = document.querySelector(`[data-project-modified="${project.id}"]`)
    if (modifiedElement) {
      modifiedElement.textContent = new Date(project.updated_at).toLocaleString()
    }
  }
  
  updateSummary(summary) {
    // Update completion percentage
    if (this.hasCompletionBarTarget) {
      this.completionBarTarget.style.width = `${summary.completion_percentage}%`
      this.completionBarTarget.setAttribute('aria-valuenow', summary.completion_percentage)
    }
    
    if (this.hasCompletionTextTarget) {
      this.completionTextTarget.textContent = `${summary.completion_percentage}%`
    }
    
    // Update decision/blocker counts
    const decisionsElement = document.querySelector('[data-decisions-count]')
    if (decisionsElement) {
      decisionsElement.textContent = summary.decisions_count
    }
    
    const blockersElement = document.querySelector('[data-blockers-count]')
    if (blockersElement) {
      blockersElement.textContent = summary.blockers_count
    }
    
    this.showNotification('Meeting summary updated', 'info')
  }
  
  updateActionItem(actionItem) {
    // Find and update the action item in the DOM
    const itemElement = document.querySelector(`[data-item-id="${actionItem.id}"]`)
    
    if (itemElement) {
      // Update status badge
      const statusBadge = itemElement.querySelector('[data-item-status-badge]')
      if (statusBadge) {
        statusBadge.textContent = actionItem.status.replace('_', ' ')
        statusBadge.className = `badge badge-${this.getStatusColor(actionItem.status)}`
      }
      
      // Update priority
      const priorityBadge = itemElement.querySelector('[data-item-priority-badge]')
      if (priorityBadge) {
        priorityBadge.textContent = `P${actionItem.priority}`
      }
      
      // Update assignee
      const assigneeElement = itemElement.querySelector('[data-item-assignee]')
      if (assigneeElement) {
        assigneeElement.textContent = actionItem.assignee || 'Unassigned'
      }
      
      // Flash the item to show it was updated
      this.flashElement(itemElement)
    }
    
    this.showNotification(`Action item "${actionItem.title}" updated`, 'info')
  }
  
  updateStats(stats) {
    if (!stats) return
    
    // Update count displays
    if (this.hasTotalItemsTarget) {
      this.totalItemsTarget.textContent = stats.total_items
    }
    
    if (this.hasCompletedItemsTarget) {
      this.completedItemsTarget.textContent = stats.completed_count
    }
    
    if (this.hasInProgressItemsTarget) {
      this.inProgressItemsTarget.textContent = stats.in_progress_count
    }
    
    if (this.hasPendingItemsTarget) {
      this.pendingItemsTarget.textContent = stats.pending_count
    }
    
    // Update completion percentage
    if (this.hasCompletionBarTarget && stats.completion_percentage !== undefined) {
      this.completionBarTarget.style.width = `${stats.completion_percentage}%`
      this.completionBarTarget.setAttribute('aria-valuenow', stats.completion_percentage)
    }
    
    if (this.hasCompletionTextTarget && stats.completion_percentage !== undefined) {
      this.completionTextTarget.textContent = `${stats.completion_percentage}%`
    }
  }
  
  updateProcessingStatus(data) {
    const processingIndicator = document.querySelector('[data-processing-indicator]')
    
    if (processingIndicator) {
      if (data.status === 'processing') {
        processingIndicator.classList.remove('hidden')
        processingIndicator.textContent = data.message || 'Processing...'
      } else if (data.status === 'complete') {
        processingIndicator.classList.add('hidden')
      } else if (data.status === 'error') {
        processingIndicator.textContent = data.message || 'Processing error'
        processingIndicator.classList.add('text-error')
      }
    }
    
    if (data.message) {
      this.showNotification(data.message, data.status === 'error' ? 'error' : 'info')
    }
  }
  
  handleAnalysisComplete(data) {
    const results = data.results
    
    let message = 'Analysis complete: '
    const parts = []
    
    if (results.action_items_created > 0) {
      parts.push(`${results.action_items_created} action items created`)
    }
    if (results.decisions_identified > 0) {
      parts.push(`${results.decisions_identified} decisions identified`)
    }
    if (results.blockers_found > 0) {
      parts.push(`${results.blockers_found} blockers found`)
    }
    
    message += parts.join(', ')
    
    this.showNotification(message, 'success')
    
    // Update stats
    if (data.stats) {
      this.updateStats(data.stats)
    }
    
    // Refresh the page after a delay to show new content
    setTimeout(() => {
      if (confirm('Analysis complete! Would you like to refresh the page to see the results?')) {
        window.location.reload()
      }
    }, 2000)
  }
  
  updateActionItemsList(actionItems) {
    // This would update a live list of action items if displayed
    const listContainer = document.querySelector('[data-action-items-live-list]')
    
    if (listContainer && actionItems.length > 0) {
      // Update or rebuild the list
      console.log(`Received ${actionItems.length} action items`)
    }
  }
  
  showNotification(message, type = 'info') {
    if (!this.hasNotificationTarget) {
      // Create notification element if it doesn't exist
      const notification = document.createElement('div')
      notification.className = 'toast toast-top toast-end z-50'
      notification.dataset.meetingUpdatesTarget = 'notification'
      document.body.appendChild(notification)
      this.notificationTarget = notification
    }
    
    // Create alert element
    const alert = document.createElement('div')
    alert.className = `alert alert-${type} shadow-lg max-w-sm`
    
    const iconMap = {
      success: '✓',
      error: '✗',
      warning: '⚠',
      info: 'ℹ'
    }
    
    alert.innerHTML = `
      <div>
        <span>${iconMap[type] || 'ℹ'}</span>
        <span>${message}</span>
      </div>
    `
    
    this.notificationTarget.appendChild(alert)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      alert.remove()
    }, 5000)
  }
  
  flashElement(element) {
    element.classList.add('animate-pulse', 'bg-yellow-100')
    setTimeout(() => {
      element.classList.remove('animate-pulse', 'bg-yellow-100')
    }, 2000)
  }
  
  getStatusColor(status) {
    const colors = {
      pending: 'ghost',
      in_progress: 'warning',
      completed: 'success',
      blocked: 'error'
    }
    return colors[status] || 'ghost'
  }
  
  // Manual refresh methods
  requestStatusUpdate() {
    if (this.channel) {
      this.channel.perform('request_status', { project_id: this.projectIdValue })
    }
  }
  
  requestActionItemsUpdate() {
    if (this.channel) {
      this.channel.perform('request_action_items', { project_id: this.projectIdValue })
    }
  }
}